import {
  ResourceStatus,
  ViewableApplicationResource,
} from 'src/app/account/components/app-resources-view/app-resources-view.component'
import { AwsEventLogAction, AwsEventLogEntry } from 'src/app/interfaces'
import { ApplicationType } from 'src/app/services/dtos'
import { ResourceType } from 'src/utils/resource-types'
import { MockApplicationOptions, mkRecoveryResource } from './showcase-mock-core'

// Quarantined resources for the unquarantine dialog. Only EC2 instances can be
// unquarantined, so all rows are ec2Instance. Mix of clean (QUARANTINED /
// SCAN_CLEAN) and unclean (SCAN_INFECTED / SCAN_FAILED) statuses so the
// "type I understand" acknowledgement flow is reachable when an unclean row is
// selected.
export function buildMockQuarantinedResources(): ViewableApplicationResource[] {
  const mk = (name: string, status: ResourceStatus) =>
    mkRecoveryResource({ name, type: ResourceType.ec2Instance, status })
  return [
    mk('web-server-01', ResourceStatus.QUARANTINED),
    mk('web-server-02', ResourceStatus.QUARANTINED),
    mk('app-server-01', ResourceStatus.SCAN_CLEAN),
    mk('app-server-02', ResourceStatus.QUARANTINED),
    mk('db-primary', ResourceStatus.SCAN_INFECTED),
    mk('cache-node-01', ResourceStatus.SCAN_FAILED),
  ]
}

// Azure quarantined resources for the unquarantine dialog (ARP-7636). Azure VMs
// can now be unquarantined, so the primary rows are azureVirtualMachine with the
// same clean/unclean status mix as the EC2 set. One scale set is included to
// mirror production: the dialog lists every quarantine-status resource, but only
// ec2Instance + azureVirtualMachine map to a resourceConfigType, so the scale set
// shows in the list yet is silently excluded from the unquarantine config write.
export function buildMockAzureQuarantinedResources(): ViewableApplicationResource[] {
  const azureId = (name: string, type: ResourceType) =>
    `/subscriptions/00000000-0000-0000-0000-000000000000/resourceGroups/mock-rg/providers/${type}/${name}`
  const vm = (name: string, status: ResourceStatus) =>
    mkRecoveryResource({
      name,
      type: ResourceType.azureVirtualMachine,
      status,
      arn: azureId(name, ResourceType.azureVirtualMachine),
    })
  return [
    vm('web-vm-01', ResourceStatus.QUARANTINED),
    vm('web-vm-02', ResourceStatus.QUARANTINED),
    vm('app-vm-01', ResourceStatus.SCAN_CLEAN),
    vm('db-vm-01', ResourceStatus.SCAN_INFECTED),
    vm('cache-vm-01', ResourceStatus.SCAN_FAILED),
    mkRecoveryResource({
      name: 'worker-scaleset',
      type: ResourceType.azureVirtualMachineScaleSet,
      status: ResourceStatus.QUARANTINED,
      arn: azureId('worker-scaleset', ResourceType.azureVirtualMachineScaleSet),
    }),
  ]
}

// -------------------------------------------------------------------------
// Retention policy screen
//
// The Indefinitely-Retained Recovery Points table only renders for accounts
// with allowProtectRecoveryPoint plus at least one protected recovery point,
// so it is effectively invisible on most dev/bugbash domains. These mocks let
// the test page preview it unconditionally.
// -------------------------------------------------------------------------

export interface SandboxScenario {
  key: string
  label: string
  // optgroup heading in the scenario picker
  group: string
  appOpts: MockApplicationOptions
  disabledCapabilities?: string[]
  siblingFailoverTest?: number
}

// launch-test-dialog: anchor is a STANDBY app (the dialog launches a test from
// standby). Covers the full radio/availability/frozen/capability matrix.
export const LAUNCH_TEST_SANDBOX_SCENARIOS: SandboxScenario[] = [
  {
    key: 'aws-off',
    label: 'AWS — sandbox off',
    group: 'AWS',
    appOpts: { type: ApplicationType.AWS, networkSandboxEnabled: false, withIgw: true, withSg: true },
  },
  {
    key: 'aws-basic',
    label: 'AWS — Security Group (basic)',
    group: 'AWS',
    appOpts: {
      type: ApplicationType.AWS,
      networkSandboxEnabled: true,
      advancedNetworkSandbox: false,
      withIgw: true,
      withSg: true,
    },
  },
  {
    key: 'aws-advanced',
    label: 'AWS — Network Firewall (advanced)',
    group: 'AWS',
    appOpts: {
      type: ApplicationType.AWS,
      networkSandboxEnabled: true,
      advancedNetworkSandbox: true,
      withIgw: true,
      withSg: true,
    },
  },
  {
    key: 'aws-advanced-no-igw',
    label: 'AWS — advanced unavailable (no Internet Gateway)',
    group: 'AWS',
    appOpts: {
      type: ApplicationType.AWS,
      networkSandboxEnabled: true,
      advancedNetworkSandbox: true,
      withIgw: false,
      withSg: true,
    },
  },
  {
    key: 'aws-basic-no-sg',
    label: 'AWS — basic unavailable (no Security Group)',
    group: 'AWS',
    appOpts: {
      type: ApplicationType.AWS,
      networkSandboxEnabled: true,
      advancedNetworkSandbox: false,
      withIgw: true,
      withSg: false,
    },
  },
  {
    key: 'aws-unavailable',
    label: 'AWS — sandbox unavailable (no SG or IGW)',
    group: 'AWS',
    appOpts: {
      type: ApplicationType.AWS,
      networkSandboxEnabled: true,
      withIgw: false,
      withSg: false,
    },
  },
  {
    key: 'aws-frozen',
    label: 'AWS — frozen (sibling app in test)',
    group: 'AWS',
    appOpts: {
      type: ApplicationType.AWS,
      networkSandboxEnabled: true,
      withIgw: true,
      withSg: true,
    },
    siblingFailoverTest: 1,
  },
  {
    key: 'aws-whitelist-off',
    label: 'AWS — allow-list capability off',
    group: 'AWS',
    appOpts: {
      type: ApplicationType.AWS,
      networkSandboxEnabled: true,
      withIgw: true,
      withSg: true,
    },
    disabledCapabilities: ['networkSandboxWhitelist'],
  },
  {
    key: 'aws-advanced-cap-off',
    label: 'AWS — advanced capability off',
    group: 'AWS',
    appOpts: {
      type: ApplicationType.AWS,
      networkSandboxEnabled: true,
      advancedNetworkSandbox: false,
      withIgw: true,
      withSg: true,
    },
    disabledCapabilities: ['advancedNetworkSandbox'],
  },
  {
    key: 'azure-on',
    label: 'Azure — sandbox on',
    group: 'Azure',
    appOpts: {
      type: ApplicationType.AZURE,
      networkSandboxEnabled: true,
      advancedNetworkSandbox: true,
      withVnet: true,
    },
  },
  {
    key: 'azure-off',
    label: 'Azure — sandbox off',
    group: 'Azure',
    appOpts: { type: ApplicationType.AZURE, networkSandboxEnabled: false, withVnet: true },
  },
  {
    key: 'azure-unavailable',
    label: 'Azure — sandbox unavailable (no Virtual Network)',
    group: 'Azure',
    appOpts: {
      type: ApplicationType.AZURE,
      networkSandboxEnabled: true,
      advancedNetworkSandbox: true,
      withVnet: false,
    },
  },
]

// application-test-config-dialog: anchor is a FAILOVER_TEST app (settings are
// edited while a test is running). No radios or RP-availability gating — the
// view is driven by the app's advanced flag, cloud, and the sandbox capability.
export const SANDBOX_CONFIG_SCENARIOS: SandboxScenario[] = [
  {
    key: 'aws-basic',
    label: 'AWS — Security Group (basic)',
    group: 'AWS',
    appOpts: { type: ApplicationType.AWS, networkSandboxEnabled: true, advancedNetworkSandbox: false },
  },
  {
    key: 'aws-advanced',
    label: 'AWS — Network Firewall (advanced)',
    group: 'AWS',
    appOpts: { type: ApplicationType.AWS, networkSandboxEnabled: true, advancedNetworkSandbox: true },
  },
  {
    key: 'aws-multi-app',
    label: 'AWS — advanced, applies to sibling apps',
    group: 'AWS',
    appOpts: { type: ApplicationType.AWS, networkSandboxEnabled: true, advancedNetworkSandbox: true },
    siblingFailoverTest: 2,
  },
  {
    key: 'aws-cap-off',
    label: 'AWS — sandbox capability off',
    group: 'AWS',
    appOpts: { type: ApplicationType.AWS, networkSandboxEnabled: true, advancedNetworkSandbox: false },
    disabledCapabilities: ['networkSandbox'],
  },
  {
    key: 'azure',
    label: 'Azure — advanced',
    group: 'Azure',
    appOpts: { type: ApplicationType.AZURE, networkSandboxEnabled: true, advancedNetworkSandbox: true },
  },
]

// Mirrors the network-firewall events the customer sees in production: TCP/443
// egress that the sandbox blocked, mostly to package / cloud-vendor SNIs.
// Used by the test page to render the event log modal in its populated state.
export function buildMockFirewallEvents(): AwsEventLogEntry[] {
  const base = Date.now()
  const make = (offsetSec: number, partial: Partial<AwsEventLogEntry>): AwsEventLogEntry => ({
    timestamp: new Date(base - offsetSec * 1000),
    srcIp: '10.131.2.63',
    srcPort: 61782,
    destIp: '185.125.188.58',
    destPort: 443,
    proto: 'TCP',
    action: AwsEventLogAction.Blocked,
    firewallName: 'mock-app-fw',
    flowId: 0,
    host: null,
    sni: null,
    ...partial,
  })
  return [
    make(0, { sni: 'api.snapcraft.io' }),
    make(3, { srcIp: '10.131.8.100', srcPort: 10573, destIp: '98.82.159.50', sni: 'api.amazon.com' }),
    make(3, {
      srcIp: '10.131.8.100',
      srcPort: 58548,
      destIp: '98.82.159.50',
      sni: 'api.amazon.com',
      flowId: 1,
    }),
    make(9, { srcIp: '10.131.7.184', srcPort: 57947, destIp: '99.80.48.20', sni: 'api.amazon.com', flowId: 2 }),
    make(14, {
      srcIp: '10.131.8.100',
      srcPort: 58548,
      destIp: '98.82.159.50',
      sni: 'api.amazon.com',
      flowId: 3,
    }),
    make(14, {
      srcIp: '10.131.8.100',
      srcPort: 58548,
      destIp: '98.82.159.50',
      sni: 'api.amazon.com',
      flowId: 4,
    }),
    make(22, { sni: 'api.snapcraft.io', flowId: 5 }),
    make(31, {
      srcIp: '10.131.7.184',
      srcPort: 39214,
      destIp: '52.46.131.27',
      sni: 's3.us-east-1.amazonaws.com',
      flowId: 6,
    }),
    make(45, {
      srcIp: '10.131.2.63',
      srcPort: 49810,
      destIp: '140.82.114.4',
      sni: 'github.com',
      flowId: 7,
    }),
    make(58, {
      srcIp: '10.131.8.100',
      srcPort: 51220,
      destIp: '34.107.221.82',
      sni: 'pkg.go.dev',
      flowId: 8,
    }),
    make(72, {
      srcIp: '10.131.7.184',
      srcPort: 60441,
      destIp: '151.101.1.140',
      sni: 'pypi.org',
      action: AwsEventLogAction.Allowed,
      flowId: 9,
    }),
    make(95, {
      srcIp: '10.131.2.63',
      srcPort: 41108,
      destIp: '52.92.176.0',
      sni: 'sts.us-east-1.amazonaws.com',
      action: AwsEventLogAction.Allowed,
      flowId: 10,
    }),
  ]
}

// -------------------------------------------------------------------------
// Recovery / Failback screen scenarios (ARP-7143)
// -------------------------------------------------------------------------
//
// One entry per row in the screen-preview picker. Each scenario carries a
// fully-realized snapshot of every input the composed screen needs:
//   - the mock Application (sync phase + sandbox/quarantine flags)
//   - the rows the resource table renders
//   - the prebuilt status object the tab-status-bar template consumes
//   - precomputed operation-notice fields (so we don't have to duplicate the
//     getter logic that lives on ApplicationComponent).
//
// Production code is never modified — the test page composes the same child
// components (<app-resources-view>, the inlined statusBarTemplate, the
// operation-notice div) the real screen does and feeds them these snapshots.
//
// Operation-notice in production reads ApplicationComponent.currentRecoveryPointTimeString /
// networkSandboxStatus / domainAllowlistStatus / etc.  Those getters depend on
// `accountService.entitledForCapability('networkSandbox')`, `quarantineEnabled`,
// `networkSandboxWhitelistEnabled` — too much wiring to replicate accurately.
// The scenario precomputes the visible booleans + strings so the test page
// binds directly without re-deriving them.
